import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { revenueApi } from "@/lib/api";
import { contractSelectionParams, isEmptySelection, type ContractSelection } from "@/lib/royalty";
import { useOrganizationStore } from "@/stores/organizationStore";

interface UseRoyaltyStatementPreviewSummaryParams {
  selection: ContractSelection;
  startDate?: Date;
  endDate?: Date;
  enabled?: boolean;
}

/**
 * Counts for a bulk royalty-statement run: picker totals, the period's
 * eligible/ineligible buckets, and what the selection would create —
 * all resolved server-side, so exact beyond the loaded page.
 */
export function useRoyaltyStatementPreviewSummary({
  selection,
  startDate,
  endDate,
  enabled = true,
}: UseRoyaltyStatementPreviewSummaryParams) {
  const { currentLabel } = useOrganizationStore();
  const periodStart = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const periodEnd = endDate ? format(endDate, "yyyy-MM-dd") : "";
  const params = contractSelectionParams(selection);
  // Nothing ticked can't be sent: the client omits an empty `contract_ids`,
  // and a request without it means "all eligible" — the opposite.
  const empty = isEmptySelection(selection);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      "royalty-statement-preview-summary",
      currentLabel?.id,
      periodStart,
      periodEnd,
      selection.mode,
      [...selection.contracts].sort().join(","),
    ],
    queryFn: () =>
      revenueApi.getRoyaltyStatementPreviewSummary({
        label_id: currentLabel?.id as string,
        period_start: periodStart,
        period_end: periodEnd,
        ...params,
      }),
    enabled:
      enabled && !!currentLabel && !!startDate && !!endDate && startDate <= endDate && !empty,
    // Ticking changes the key; keep the last counts on screen mid-fetch.
    placeholderData: keepPreviousData,
  });

  // Nothing ticked: the label/eligibility figures from the last response still
  // hold (they ignore the selection), but the selection figures must read zero
  // rather than the placeholder's stale values.
  const summary =
    empty && data ? { ...data, selected_contracts: 0, payees: 0, statements_to_create: 0 } : data;
  return { summary, isLoading, isError, isFetching };
}
